import 'styles/join-us/job-board.scss';

import classNames from 'classnames';
import sortBy from 'lodash/sortBy';
import PropTypes from 'prop-types';
import React from 'react';
import ReactSelect from 'react-select';

const ALL_OFFICES = 'All Locations';
const ALL_DEPARTMENTS = 'All Departments';

function doesOfficeHaveJobs(office) {
  return office.departments.some(doesDepartmentHaveJobs);
}

function doesDepartmentHaveJobs(department) {
  return department.jobs.length > 0;
}

export default class JobBoard extends React.Component {
  static propTypes = {
    offices: PropTypes.array.isRequired,
  };

  state = {
    filter: '',
    filterRegex: undefined,
    officeId: ALL_OFFICES,
    departmentId: ALL_DEPARTMENTS,
  };

  constructor(props) {
    super(props);

    this.offices = this.props.offices.filter(doesOfficeHaveJobs);
    this.offices = sortBy(this.offices, ['name']);
    this.officesById = this.offices.reduce((map, office) => {
      map[office.id] = office;
      return map;
    }, {});
    this.officeOptions = this.buildOfficeOptions(this.offices);
    this.departmentOptionsByOfficeId = {};
  }

  handleOfficeChange = option => {
    this.setState({ officeId: option.value, departmentId: ALL_DEPARTMENTS });
  };

  handleDepartmentChange = option => {
    this.setState({ departmentId: option.value });
  };

  handleFilterChange = event => {
    const filter = event.target.value;

    this.setState({
      filter,
      filterRegex: new RegExp(filter.trim().toLowerCase(), 'i'),
    });
  };

  buildOfficeOptions(offices) {
    const officeOptions = offices.map(office => ({
      label: office.name,
      value: office.id,
    }));

    return [{ label: ALL_OFFICES, value: ALL_OFFICES }].concat(officeOptions);
  }

  getDepartmentOptions(officeId) {
    if (!this.departmentOptionsByOfficeId[officeId]) {
      this.departmentOptionsByOfficeId[officeId] = this.buildDepartmentOptions(
        officeId
      );
    }

    return this.departmentOptionsByOfficeId[officeId];
  }

  buildDepartmentOptions(officeId) {
    const options = [{ label: ALL_DEPARTMENTS, value: ALL_DEPARTMENTS }];

    const office = this.officesById[officeId];
    if (!office) {
      return options;
    }

    const officeOptions = office.departments
      .filter(doesDepartmentHaveJobs)
      .map(department => ({
        label: department.name,
        value: department.id,
      }));

    return options.concat(officeOptions);
  }

  renderOffice = office => (
    <Office
      key={office.id}
      office={office}
      departmentId={this.state.departmentId}
      filterRegex={this.state.filterRegex}
    />
  );

  renderOffices() {
    if (this.state.officeId === ALL_OFFICES) {
      return this.offices.map(this.renderOffice);
    }

    const office = this.officesById[this.state.officeId];
    return this.renderOffice(office);
  }

  renderSelect({ className, ...props }) {
    return (
      <ReactSelect
        className={classNames('JobBoard__select', className)}
        clearable={false}
        searchable={false}
        {...props}
      />
    );
  }

  render() {
    const departmentOptions = this.getDepartmentOptions(this.state.officeId);

    return (
      <div>
        <div className="JobBoard__controls">
          <input
            className="JobBoard__filter-input"
            onChange={this.handleFilterChange}
            placeholder="Search"
            type="text"
            value={this.state.filter}
          />
          {this.renderSelect({
            className: 'JobBoard__office-select',
            value: this.state.officeId,
            options: this.officeOptions,
            onChange: this.handleOfficeChange,
          })}
          {this.renderSelect({
            className: 'JobBoard__department-select',
            value: this.state.departmentId,
            options: departmentOptions,
            onChange: this.handleDepartmentChange,
            disabled: departmentOptions.length <= 1,
          })}
        </div>
        <div className="JobBoard__content">{this.renderOffices()}</div>
      </div>
    );
  }
}

class Office extends React.Component {
  static propTypes = {
    departmentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
      .isRequired,
    filterRegex: PropTypes.instanceOf(RegExp),
    office: PropTypes.object.isRequired,
  };

  render() {
    const departmentId = this.props.departmentId;

    return this.props.office.departments
      .filter(
        department =>
          departmentId === ALL_DEPARTMENTS || department.id === departmentId
      )
      .map(department => (
        <Department
          key={department.id}
          department={department}
          filterRegex={this.props.filterRegex}
        />
      ));
  }
}

class Department extends React.Component {
  static propTypes = {
    department: PropTypes.object.isRequired,
    filterRegex: PropTypes.instanceOf(RegExp),
  };

  get jobs() {
    const { department, filterRegex } = this.props;
    let jobs = department.jobs;

    if (filterRegex) {
      jobs = jobs.filter(
        job =>
          filterRegex.test(job.title) || filterRegex.test(job.location.name)
      );
    }

    return jobs;
  }

  render() {
    const department = this.props.department;

    if (this.jobs.length === 0) {
      return null;
    }

    return (
      <div key={department.id} className="JobBoard__department">
        <div className="new-h6 navy uppercase JobBoard__department-name">
          {department.name}
        </div>
        <div className="JobBoard__department-jobs">
          {this.jobs.map(job => (
            <Job key={job.id} department={department} job={job} />
          ))}
        </div>
      </div>
    );
  }
}

class Job extends React.Component {
  static propTypes = {
    department: PropTypes.object.isRequired,
    job: PropTypes.object.isRequired,
  };

  render() {
    const { job, department } = this.props;

    return (
      <div className="JobBoard__job" key={job.id}>
        <a
          href={job.absolute_url}
          className="flat-blue JobBoard__job-title"
          rel="noopener noreferrer"
          target="_blank"
        >
          {job.title}
        </a>
        <div className="new-text navy JobBoard__job-department">
          {department.name}
        </div>
        <div className="new-text navy JobBoard__job-location">
          {job.location.name}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./app/javascript/join-us/components/JobBoard.js